import { Server as McpSDKServer } from '@modelcontextprotocol/sdk/server/index.js';
import {
  CallToolRequestSchema,
  ErrorCode,
  ListToolsRequestSchema,
  McpError,
} from '@modelcontextprotocol/sdk/types.js';
import type { IMcpServer, ISoraVideoTool } from '~/interfaces/IMcpServer.js';
import type { ListToolsResult, CallToolRequest } from '@modelcontextprotocol/sdk/types.js';

/**
 * 这是一个抽象基类，它实现了我们定义的 IMcpServer 接口。
 * 它封装了所有 MCP 服务器通用的、与具体通信方式（如HTTP）无关的逻辑。
 * 就像一个房子的毛坯房，提供了基础结构，但还没装门窗。
 */
export abstract class BaseMcpServer implements IMcpServer {
  // `protected` 意味着这个属性只能被这个类自己和继承它的子类访问
  protected sdkServer: McpSDKServer;
  protected tools: Map<string, ISoraVideoTool> = new Map();
  protected state: 'idle' | 'running' | 'stopping' = 'idle';

  constructor() {
    // 初始化 MCP SDK 的核心服务器实例
    this.sdkServer = new McpSDKServer({
      name: 'sora-video-mcp-server',
      version: '1.0.0', // 理想情况下，这个版本应该从 package.json 读取
    });

    this.sdkServer.registerCapabilities({
      tools: {
        listChanged: false,
      },
    });

    // --- 设置 SDK 的请求处理器 ---
    // 当 SDK 收到一个 'tools/list' 请求时，它会执行这个函数
    this.sdkServer.setRequestHandler(ListToolsRequestSchema, async () => {
      console.log('[BaseServer] Handling "tools/list" request.');

      const toolDefinitions: ListToolsResult['tools'] = Array.from(this.tools.values()).map(tool => {
        const { run, ...definition } = tool;
        return definition;
      });

      return { tools: toolDefinitions } satisfies ListToolsResult;
    });

    // 当 SDK 收到一个 'tools/call' 请求时，它会执行这个函数
    this.sdkServer.setRequestHandler(CallToolRequestSchema, async (request: CallToolRequest) => {
      const { name, arguments: args } = request.params;
      console.log(`[BaseServer] Handling "tools/call" request for tool: ${name}`);

      const tool = this.tools.get(name);
      if (!tool) {
        console.error(`[BaseServer] Tool not found: ${name}`);
        throw new McpError(ErrorCode.InvalidParams, `Tool "${name}" not found.`);
      }

      const resolvedArgs = args ?? {};
      return tool.run(resolvedArgs);
    });
  }

  // 实现接口规定的 registerTool 方法
  public registerTool(tool: ISoraVideoTool): void {
    if (this.tools.has(tool.name)) {
      console.warn(`[BaseServer] Tool "${tool.name}" is already registered. Overwriting.`);
    }
    this.tools.set(tool.name, tool);
    console.log(`[BaseServer] Tool registered: ${tool.name}`);
  }

  // --- 抽象方法 ---
  // `abstract` 意味着这个方法只在这里声明，具体的实现必须由子类来完成。
  // 这就像蓝图上写着“这里要有一扇门”，但具体是木门还是铁门，由施工队（子类）决定。
  
  /**
   * 启动服务器。具体如何“启动”（比如监听一个端口），由子类实现。
   */
  public abstract start(): Promise<void>;

  /**
   * 停止服务器。具体如何“停止”，也由子类实现。
   */
  public abstract stop(): Promise<void>;
}
